import { TranslateFunction } from '~/Locale';
import { ElectricCarGrantFormData } from './types';
import { ELIGIBLE_MUNICIPALITIES } from './constants';
import { formatMoneyWithoutCurrency } from '~/helpers/currency';

export const MAX_VEHICLE_PRICE = 500_000;
export const MIN_VEHICLE_PRICE = 54_000;

export const MAX_LEASING_FEE = 4_700;

/**
 * Approximate income thresholds (kr/year) per household type.
 * Based on ~80% of median income. Exact values to be determined by
 * Naturvårdsverket based on SCB statistics.
 */
export const INCOME_THRESHOLDS = {
  single: 320_000,
  couple: 480_000,
  family: 550_000
} as const;

export interface EligibilityResult {
  eligible: boolean;
  reasons: string[];
}

export const checkEligibility = (
  data: ElectricCarGrantFormData,
  t: TranslateFunction
): EligibilityResult => {
  const reasons: string[] = [];

  const isMunicipalityEligible = data.municipality?.value
    ? ELIGIBLE_MUNICIPALITIES.some(m => m.value === data.municipality.value)
    : false;
  if (!isMunicipalityEligible) {
    reasons.push(
      t(
        '%s does not count as a rural or sparsely populated area. Only rural and sparsely populated municipalities are eligible for the electric car premium',
        data.municipality?.label
      )
    );
  }

  if (!data.residenceDuration) {
    reasons.push(
      t(
        'You must be registered in the municipality for at least four months after applying'
      )
    );
  }

  const householdType = data.householdType?.value as
    | 'single'
    | 'couple'
    | 'family';
  const incomeThreshold =
    householdType && householdType in INCOME_THRESHOLDS
      ? INCOME_THRESHOLDS[householdType]
      : INCOME_THRESHOLDS.single;
  const income = data.annualHouseholdIncome ?? 0;
  if (income > incomeThreshold) {
    reasons.push(
      t(
        'Yearly income of %s kr exceeds the limit of %s kr/year for your household type',
        formatMoneyWithoutCurrency(income),
        formatMoneyWithoutCurrency(incomeThreshold)
      )
    );
  }

  if (data.stateIncomeTax?.value === 'yes') {
    reasons.push(
      t(
        'If anyone in the household pays state income tax, the electric car premium is not eligible'
      )
    );
  }

  if (data.previousEvPhevOwnership?.value === 'yes') {
    reasons.push(
      t(
        'The household must not have owned or leased an electric car or plug-in hybrid in the last 12 months'
      )
    );
  }

  if (data.businessUse?.value === 'yes') {
    reasons.push(t('The car may not be used for business purposes'));
  }

  if (data.previousOwnershipByHousehold?.value === 'yes') {
    reasons.push(
      t(
        'The car may not have been owned or leased by the household or close relatives in the last 12 months'
      )
    );
  }

  if (data.outstandingVehicleRelatedDebts?.value === 'yes') {
    reasons.push(
      t('The vehicle may not have any outstanding debt related to vehicle tax')
    );
  }

  if (data.ownershipType?.value === 'purchase') {
    const price = data.electricCarPurchasePrice ?? 0;
    if (price > MAX_VEHICLE_PRICE) {
      reasons.push(
        t(
          'Vehicle price %s kr exceeds the maximum of %s kr',
          formatMoneyWithoutCurrency(price),
          formatMoneyWithoutCurrency(MAX_VEHICLE_PRICE)
        )
      );
    }
  }

  if (data.ownershipType?.value === 'lease') {
    const leasingFee = data.leasingFeePerMonth ?? 0;
    if (leasingFee > MAX_LEASING_FEE) {
      reasons.push(
        t(
          'Leasing fee %s kr/month exceeds the maximum of %s kr/month',
          formatMoneyWithoutCurrency(leasingFee),
          formatMoneyWithoutCurrency(MAX_LEASING_FEE)
        )
      );
    }
  }

  return {
    eligible: reasons.length === 0,
    reasons
  };
};
